import {EditTable, Fields} from "./Fields";
import SelectNumberNullable from "./SelectNumberNullable";
import Input from "./Input";
import SelectNumber from "./SelectNumber";
import Dialog from "./Dialog";
import React, {useContext, useEffect, useState} from "react";
import Language from "../controllers/Language";
import {useValidation} from "../validation";
import CarbohydrateController from "../controllers/CarbohydrateController";
import Success from "./Success";
import AppContext from "../appContext";
import LanguageController from "../controllers/LanguageController";
import CheckBox from "./CheckBox";
import AgronomistController from "../controllers/AgronomistController";
import FirestoreAgronomist from "../controllers/FirestoreAgronomist";
import DistributorController from "../controllers/DistributorController";
import Distributor from "../controllers/Distributor";
import {formInputs} from "./FormInputs";
import CarboReportRequest from "../controllers/CarboReportRequest";
import AddressController from "../controllers/AddressController";
import AddressList from "../controllers/AddressList";
import MfwSuccess from "../controllers/MfwSuccess";
import MFWSuccessDialog from "./MFWSuccessDialog";



const DownloadSelectedReport: React.FC<{
    showSettings: boolean;
    setShowSettings: (v: boolean) => void;
    checkedCarbos: { group: number, id: number }[];
}> = (props) => {
    const context = useContext(AppContext)
    const [languages, setLanguages] = useState<Language[]>([])
    const [selectedLanguage, setSelectedLanguage] = useState<number | null>(null)
    const [includeSummary, setIncludeSummary] = useState(true)
    const [includeFarmSummary, setIncludeFarmSummary] = useState(true)
    const [lowImageQuality, setLowImageQuality] = useState(false)
    
    const [clientName, setClientName] = useState("")
    const [farmName, setFarmName] = useState("")
    const [agronomist, setAgronomist] = useState<FirestoreAgronomist | null>(null)
    const [agent, setAgent] = useState("")
    const [distributorId, setDistributorId] = useState<number | null>(null)
    const [addressId, setAddressId] = useState<number | null>(null)
    
    const [agronomistsList, setAgronomistsList] = useState<FirestoreAgronomist[]>([])
    const [distributors, setDistributors] = useState<Distributor[]>([])
    const [addresses, setAddresses] = useState<AddressList[]>([])
    
    const [successData, setSuccessData] = useState<MfwSuccess[] | null>(null);

    const validation = useValidation({
        language: () => selectedLanguage != null,
        client: () => clientName.length > 0,
        farm: () => farmName.length > 0,
        agent: () => agent.length > 0,
        agronomist: () => agronomist != null,
        distributor: () => distributorId != null,
        address: () => addressId != null,
    })
    
    useEffect(() => {
        const hideLoader = context.showLoader()
        Promise.all([
            AgronomistController.index().then(resp => {
                setAgronomistsList(resp.data)
            }),
            DistributorController.all().then(resp => {
                setDistributors(resp.data)
            }),
            LanguageController.index().then(resp => setLanguages(resp.data)),
            AddressController.index().then(resp => setAddresses(resp.data))
        ]).finally(hideLoader)
    }, [])

    useEffect(() => {
        const firstCarbo = props.checkedCarbos[0]

        if (firstCarbo != undefined) {
            const hideLoader = context.showLoader()
            
        CarbohydrateController.getLabResult({id: firstCarbo.id}).then(resp => {
                const foundAgronomist = agronomistsList.find(a => a.name?.toLowerCase() == resp.data.agronomist?.toLowerCase())
                if (foundAgronomist != undefined)
                    setAgronomist(foundAgronomist)
                setClientName(resp.data.entity ?? "")
                setFarmName(resp.data.farmName ?? "")
                setAgent(resp.data.agent ?? "")
            }).finally(hideLoader)
        }
    }, [props.showSettings]);

    function closeDownloadPopup() {
        props.setShowSettings(false)
        setSelectedLanguage(null)
    }

    const formRef = React.createRef<HTMLFormElement>();
    
    function downloadSelectedBlocksReport() {
        if (!validation.validate() || selectedLanguage == null) return;

        formRef.current?.submit();
        context.showSnack(<Success title={"Starting download"}/>)
    }
    
    function uploadSelectedBlocksReport() {
        if (!validation.validate() || selectedLanguage == null) return;
        
        const loader = context.showLoader()
        
        CarbohydrateController.postToMfw({
            result: props.checkedCarbos.map(r => `${r.group},${r.id}`).join(';'),
            agriculturalist: agronomist?.name ?? "",
            client: clientName,
            distributor: distributorId ?? 0,
            farm: farmName,
            includeSummary: includeSummary,
            includeFarmSummary: includeFarmSummary,
            langId: selectedLanguage,
            addressId: addressId ?? 0,
            agent,
            lowImageQuality: lowImageQuality,
        }).then(resp => {
            setSuccessData(resp.data)
        })
        .finally(() => {
            loader()
        })
    }
    
    
    
    return <Dialog title="Download selected report"
                   show={props.showSettings}
                   setShow={closeDownloadPopup}>

        <form ref={formRef} action="/api/carbohydrate/blockreport" method="post" target="_blank">

            {formInputs<CarboReportRequest>({
                result: props.checkedCarbos.map(r => `${r.group},${r.id}`).join(';'),
                agriculturalist: agronomist?.name ?? "",
                client: clientName,
                distributor: distributorId?.toString() ?? "",
                farm: farmName,
                includeSummary: includeSummary.toString(),
                includeFarmSummary: includeFarmSummary.toString(),
                langId: selectedLanguage?.toString() ?? "",
                addressId: addressId?.toString() ?? "",
                lowImageQuality: lowImageQuality.toString(),
                agent,
            })}
        </form>
        
        <EditTable footer={
            <div className="btn bg-orange-500 text-wrap" onClick={uploadSelectedBlocksReport}>
                Upload to MFW
            </div>
        } discard={closeDownloadPopup}
                   save={() => downloadSelectedBlocksReport()}
                   saveWord="Download">
            
            <div className="border m-2 p-2 border-primary shadow-md w-[600px]">
                <Fields columns={1} fields={[
                    {
                        label: 'Language',
                        value: <SelectNumberNullable
                            className={!validation.rules.language ? "border border-error-600" : ""}
                            options={languages}
                            textFunc={t => t.name ?? ""}
                            valueFunc={v => v.id}
                            value={selectedLanguage}
                            onChange={v => setSelectedLanguage(v)}/>,
                    },
                    {
                        label: 'Client Name',
                        value: <Input className={!validation.rules.client ? "border border-error-600" : ""} 
                                      value={clientName} 
                                      change={v => setClientName(v)}/>
                    },
                    {
                        label: 'Farm Name',
                        value: <Input className={!validation.rules.farm ? "border border-error-600" : ""}
                                      value={farmName} 
                                      change={v => setFarmName(v)}/>
                    },
                    {
                        label: 'Agriculturist',
                        value: <SelectNumber options={agronomistsList}
                                             className={!validation.rules.agronomist ? "border border-error-600" : ""}
                                             textFunc={t => t.name ?? ""}
                                             valueFunc={v => v.id}
                                             value={agronomist?.id ?? 0}
                                             onChange={v => setAgronomist(agronomistsList.find(f => f.id == v) ?? null)}/>
                    },
                    {
                        label: 'Agent',
                        value: <Input className={!validation.rules.agent ? "border border-error-600" : ""}
                                      value={agent} 
                                      change={v => setAgent(v)}/>
                    },
                    {
                        label: 'Distributors',
                        value: <SelectNumberNullable options={distributors}
                                                     className={!validation.rules.distributor ? "border border-error-600" : ""}
                                                     textFunc={r => r.name ?? ''} 
                                                     valueFunc={r => r.id} value={distributorId} 
                                                     onChange={v => setDistributorId(distributors.find(d => d.id == v)?.id ?? null)}/>
                    },
                    {
                        label: 'Address',
                        value: <SelectNumberNullable options={addresses}
                                                     className={!validation.rules.address ? "border border-error-600" : ""}
                                                     textFunc={r => r.name ?? ''} 
                                                     valueFunc={r => r.id} value={addressId} 
                                                     onChange={v => setAddressId(addresses.find(d => d.id == v)?.id ?? null)}/>
                    },
                    {
                        label: <div className={"flex justify-start items-center"}>
                            <div>Low Image Quality</div>
                            <div className="ml-2" title="Low image quality will reduce the size of the report">
                                <svg height={20} width={20} fill="none" viewBox="0 0 24 24"
                                     stroke-width="1.5" stroke="gray">
                                    <path stroke-linecap="round" stroke-linejoin="round"
                                          d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/>
                                </svg>
                            </div>
                        </div>,
                        value: <CheckBox checked={lowImageQuality} onChange={v => {
                            setLowImageQuality(v)
                        }}/>
                    },
                    {
                        label: 'Include Summary',
                        value: <CheckBox checked={includeSummary} onChange={v => {
                            setIncludeSummary(v)
                        }}/>
                    },
                    {
                        label: 'Include Farm Summary',
                        value: <CheckBox checked={includeFarmSummary} onChange={v => {
                            setIncludeFarmSummary(v)
                        }}/>
                    },
                ]}/>
            </div>

        </EditTable>
        <MFWSuccessDialog data={successData} close={() => setSuccessData(null)} />
    </Dialog>
}

export default DownloadSelectedReport;